import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Settings, Shield } from 'lucide-react';

export default function CookiesPage() {
  return (
    <div className="min-h-screen bg-slate-50" data-testid="cookies-page">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <ArrowLeft size={18} className="text-slate-500" />
            <span className="text-sm text-slate-600">Volver a inicio</span>
          </Link>
          <Link to="/">
            <img 
              src="https://customer-assets.emergentagent.com/job_baja-laboral-riesgos/artifacts/m7tg5pgh_logo.jpg" 
              alt="PrevenciónLegal @tecnoabogado" 
              className="h-10 w-auto"
            />
          </Link>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16">
        <div className="flex items-center gap-3 mb-8">
          <Cookie className="text-rose-600" size={32} />
          <h1 className="text-3xl font-bold text-slate-900">Política de Cookies</h1>
        </div>
        <p className="text-sm text-slate-500 mb-8">Última actualización: Abril 2026</p>

        <div className="prose prose-slate max-w-none space-y-8">
          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">¿Qué son las cookies?</h2>
            <p className="text-slate-600">Las cookies son pequeños archivos de texto que se almacenan en su dispositivo (ordenador, tablet, smartphone) cuando visita un sitio web. Permiten que el sitio web recuerde sus acciones y preferencias durante un período de tiempo, mejorando su experiencia de navegación.</p>
            <p className="text-slate-600 mt-4">En cumplimiento de la <strong>Ley 34/2002 de Servicios de la Sociedad de la Información y Comercio Electrónico (LSSI)</strong>, el <strong>Reglamento General de Protección de Datos (RGPD UE 2016/679)</strong>, y las directrices de la <strong>Agencia Española de Protección de Datos (AEPD)</strong>, le informamos sobre las cookies que utiliza este sitio web.</p>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Tipos de cookies que utilizamos</h2>
            
            <div className="bg-white border border-slate-200 p-6 mb-4">
              <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                <Shield size={18} className="text-green-600" />
                1. Cookies técnicas (necesarias)
              </h3>
              <p className="text-slate-600 mb-4">Son imprescindibles para el funcionamiento del sitio web. <strong>No requieren consentimiento previo</strong> según el Art. 22.2 LSSI.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-2 border">Cookie</th>
                      <th className="text-left p-2 border">Finalidad</th>
                      <th className="text-left p-2 border">Duración</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-2 border font-mono text-xs">access_token</td>
                      <td className="p-2 border">Autenticación de usuario</td>
                      <td className="p-2 border">1 hora</td>
                    </tr>
                    <tr>
                      <td className="p-2 border font-mono text-xs">refresh_token</td>
                      <td className="p-2 border">Mantener sesión activa</td>
                      <td className="p-2 border">7 días</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white border border-slate-200 p-6">
              <h3 className="font-bold text-slate-900 mb-3 flex items-center gap-2">
                <Settings size={18} className="text-amber-600" />
                2. Cookies de preferencias
              </h3>
              <p className="text-slate-600 mb-4">Permiten recordar sus preferencias de navegación.</p>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-2 border">Cookie</th>
                      <th className="text-left p-2 border">Finalidad</th>
                      <th className="text-left p-2 border">Duración</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="p-2 border font-mono text-xs">theme</td>
                      <td className="p-2 border">Preferencia de tema visual</td>
                      <td className="p-2 border">1 año</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Cómo gestionar las cookies</h2>
            <p className="text-slate-600 mb-4">Puede permitir, bloquear o eliminar las cookies instaladas en su dispositivo mediante la configuración de su navegador:</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600">
              <li><strong>Google Chrome:</strong> Configuración → Privacidad y seguridad → Cookies</li>
              <li><strong>Mozilla Firefox:</strong> Opciones → Privacidad y seguridad → Cookies</li>
              <li><strong>Safari:</strong> Preferencias → Privacidad → Gestionar datos de sitios web</li>
              <li><strong>Microsoft Edge:</strong> Configuración → Cookies y permisos del sitio</li>
            </ul>
            <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mt-4">
              <p className="text-amber-800"><strong>Importante:</strong> Si deshabilita las cookies técnicas, algunas funcionalidades del sitio web (como el inicio de sesión) podrían no funcionar correctamente.</p>
            </div>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Base legal y derechos</h2>
            <p className="text-slate-600 mb-4">El tratamiento de datos personales a través de cookies se basa en:</p>
            <ul className="list-disc pl-6 space-y-2 text-slate-600">
              <li><strong>Interés legítimo (Art. 6.1.f RGPD)</strong> para cookies técnicas necesarias</li>
              <li><strong>Consentimiento informado (Art. 6.1.a RGPD)</strong> para cookies analíticas (si se implementan)</li>
            </ul>
            <p className="text-slate-600 mt-4">Puede ejercer sus derechos de acceso, rectificación, supresión, limitación, portabilidad y oposición contactando con:</p>
            <ul className="mt-2 space-y-1 text-slate-600">
              <li><strong>Responsable:</strong> José Luis Vilaplana</li>
              <li><strong>Email:</strong> <a href="mailto:info@tecnoabogado.com" className="text-rose-600 hover:underline">info@tecnoabogado.com</a></li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-bold text-slate-900 mb-4">Documentos relacionados</h2>
            <ul className="space-y-2">
              <li><Link to="/legal/privacidad" className="text-rose-600 hover:underline">→ Política de Privacidad</Link></li>
              <li><Link to="/legal/aviso-legal" className="text-rose-600 hover:underline">→ Aviso Legal</Link></li>
            </ul>
          </section>
        </div>
      </main>

      {/* Footer simple */}
      <footer className="bg-slate-900 text-slate-400 py-8 px-6">
        <div className="max-w-4xl mx-auto text-center text-sm">
          <p>© 2026 vilaplana@tecnoabogado. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
}
